import { api } from '@packages/backend/convex/_generated/api';
import { type PrintJob, printJobStrictSchema } from '@packages/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from 'convex-helpers/react';
import { TriangleAlert } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { LoadingIcon } from '@/components/ui+/LoadingIcon';
import { PrintJobDetails } from './components/PrintJobDetails';
import { PrintJobsList } from './components/PrintJobsList';

export const Route = createFileRoute('/r/$restaurantId/print-jobs')({
  validateSearch: printJobStrictSchema.pick({ _id: true }),
  component: Component,
});

function Component() {
  const [selectedPrintJob, setSelectedPrintJob] = useState<PrintJob | null>(null);
  const { restaurantId } = Route.useParams();
  const { _id } = Route.useSearch();
  const posConfigQuery = useQuery(api.posConfigs.getByRestaurantId, { restaurantId });
  const { data, isError, isPending } = useQuery(api.printJobs.listByRestaurantId, {
    restaurantId,
  });
  const printJobs: PrintJob[] | undefined = data;

  useEffect(() => {
    if (!printJobs) return;
    if (_id) {
      setSelectedPrintJob(printJobs.find((pj) => pj._id === _id) ?? null);
    } else {
      setSelectedPrintJob(null);
    }
  }, [_id, printJobs]);

  if (isPending || printJobs === undefined) return <LoadingIcon />;
  if (isError) return <div>Error</div>;

  return (
    <div className="grid md:grid-cols-2 p-6 min-h-[calc(100vh-(--spacing(16)))] flex-1 flex-col bg-muted/60 dark:bg-muted/20">
      {/* Left column */}
      <div className="md:pr-3">
        {posConfigQuery.data && !posConfigQuery.data.printJobLoggingEnabled && (
          <Alert className="mb-6" variant="destructive">
            <TriangleAlert className="h-4 w-4" />
            <AlertTitle>Print logging is disabled</AlertTitle>
            <AlertDescription>
              <Link
                className={'underline'}
                from={'/r/$restaurantId/print-jobs'}
                to={'/r/$restaurantId/s/pos-printers'}
              >
                settings
              </Link>
            </AlertDescription>
          </Alert>
        )}
        <PrintJobsList printJobs={printJobs} restaurantId={restaurantId} selectedPrintJobId={_id} />
      </div>

      {/* Right column */}
      <div className="md:pl-3">
        {selectedPrintJob ? (
          <PrintJobDetails printJob={selectedPrintJob} />
        ) : (
          <div className="text-muted-foreground text-center mt-8">
            Select a print job to view details
          </div>
        )}
      </div>
    </div>
  );
}
